import React, { Dispatch, SetStateAction, useState, useMemo, useEffect } from "react";
import { Table, Card, Space, Select, Button, Empty, Skeleton, Checkbox, Dropdown, Menu } from "antd";
import { UpOutlined, DownOutlined, MoreOutlined } from "@ant-design/icons";
import type { ColumnsType, TableProps } from "antd/es/table";
import type { MenuProps } from "antd";
import "./PreviewGrid.css";

type GridViewProps = {
  columns: string[];
  data: any[];
  isLoading?: boolean;
  enableSelection?: boolean;
  multiRowSelection?: boolean;
  onPaginationChange?: Dispatch<
    SetStateAction<{ pageIndex: number; pageSize: number }>
  >;
  pagination?: {
    pageIndex: number;
    pageSize: number;
  };
  serverSidePagination?: boolean;
  rowCount?: number;
  selectedRows: Record<string, any>[] | undefined;
  onSelectRows: (rows: Record<string, any>[]) => void | undefined;
  view?: 'table' | 'card';
  onViewChange?: (view: 'table' | 'card') => void;
  rowActions?: any[];
  onRowAction?: (action: any, row: any) => void;
  actionLoading?: string;
};

export function PreviewGrid({
  columns,
  data,
  isLoading,
  selectedRows,
  onSelectRows,
  enableSelection = false,
  multiRowSelection = false,
  onPaginationChange,
  serverSidePagination,
  pagination,
  rowCount,
  view = 'table',
  onViewChange,
  rowActions,
  onRowAction,
  actionLoading,
}: GridViewProps) {
  
  console.log('🔧 PreviewGrid: Component rendering with data:', data?.length, 'rows');

  // Helper function to determine if a value is numeric
  const isNumeric = (value: any): boolean => {
    return !isNaN(parseFloat(value)) && isFinite(value);
  };

  // Helper function to safely convert value for comparison
  const getSortableValue = (value: any): string | number => {
    if (value === null || value === undefined) return '';
    
    const stringValue = String(value).trim();
    
    // Check if it's a number
    if (isNumeric(stringValue)) {
      return parseFloat(stringValue);
    }
    
    // Return lowercase string for case-insensitive sorting
    return stringValue.toLowerCase();
  };

  // Convert string columns to Ant Design table columns
  const tableColumns: ColumnsType<any> = useMemo(() => {
    const dataColumns = columns.map((col: string) => ({
      title: col,
      dataIndex: col,
      key: col,
      sorter: (a: any, b: any) => {
        const aVal = getSortableValue(a[col]);
        const bVal = getSortableValue(b[col]);
        
        // Handle mixed types (numbers vs strings)
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return aVal - bVal;
        }
        
        // String comparison
        const aStr = String(aVal);
        const bStr = String(bVal);
        
        return aStr.localeCompare(bStr, undefined, { 
          numeric: true, 
          sensitivity: 'base' 
        });
      },
      ellipsis: false,
      render: (text: any) => (
        <span style={{ color: 'var(--main-text)', fontWeight: 500 }}>
          {String(text ?? '')}
        </span>
      ),
    }));

    // Add Actions column if row actions are provided
    if (rowActions && rowActions.length > 0 && onRowAction) {
      dataColumns.push({
        title: 'Actions',
        key: 'actions',
        fixed: 'right',
        width: 80,
        render: (_: any, record: any) => {
          const items: MenuProps['items'] = rowActions.map((action: any) => ({
            key: action.id,
            label: actionLoading === action.id ? 'Loading...' : (action.label || 'Action'),
            disabled: actionLoading === action.id,
            onClick: () => onRowAction(action, record),
          }));

          return (
            <Dropdown 
              menu={{ items }} 
              trigger={['click']}
              placement="bottomRight"
            >
              <Button 
                type="text" 
                icon={<MoreOutlined />} 
                style={{ 
                  color: 'var(--main-text)',
                  border: 'none',
                }}
              />
            </Dropdown>
          );
        },
      } as any);
    }

    return dataColumns;
  }, [columns, rowActions, onRowAction, actionLoading]);

  // Generate unique row keys and filter out empty rows
  const dataWithKeys = useMemo(() => {
    return (data || [])
      .filter((item) => {
        // Filter out empty rows - check if all values are empty/null/undefined/whitespace
        if (!item || typeof item !== 'object') return false;
        const values = Object.values(item);
        return values.some(value => {
          if (value === null || value === undefined) return false;
          const stringValue = String(value).trim();
          return stringValue !== '';
        });
      })
      .map((item, index) => ({
        ...item,
        key: item.key || item.id || index,
      }));
  }, [data]);

  // Simple CSS injection approach
  useEffect(() => {
    // Inject CSS once when component mounts
    let styleElement = document.getElementById('force-radio-styles');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'force-radio-styles';
      styleElement.textContent = [
        'body .ant-table .ant-radio .ant-radio-inner,',
        'body .ant-table-tbody .ant-radio .ant-radio-inner,',
        'body .ant-radio-wrapper .ant-radio .ant-radio-inner,',
        'body span.ant-radio .ant-radio-inner,',
        'body .ant-radio-inner {',
        '  background: var(--radio-bg) !important;',
        '  background-color: var(--radio-bg) !important;',
        '  opacity: 1 !important;',
        '}',
        'body .ant-table .ant-radio-input,',
        'body .ant-radio-input {',
        '  opacity: 0 !important;',
        '  visibility: hidden !important;',
        '  position: absolute !important;',
        '  z-index: -1 !important;',
        '}',
        'body .ant-table-tbody > tr:hover > td,',
        'body .ant-table .ant-table-tbody > tr:hover > td {',
        '  background: var(--table-row-hover) !important;',
        '  background-color: var(--table-row-hover) !important;',
        '}',
        'body .ant-radio-checked .ant-radio-inner {',
        '  background: var(--radio-bg) !important;',
        '  background-color: var(--radio-bg) !important;',
        '  border-color: var(--radio-checked-bg) !important;',
        '}',
        'body .ant-radio-checked .ant-radio-inner::after {',
        '  background: var(--radio-checked-bg) !important;',
        '  background-color: var(--radio-checked-bg) !important;',
        '}',
        'tr.ant-table-measure-row[aria-hidden="true"] {',
        '  display: none !important;',
        '}',
      ].join('\n');
      document.head.appendChild(styleElement);
      console.log('🔧 PreviewGrid: CSS injected');
    }

    return () => {
      // Clean up on unmount
      const style = document.getElementById('force-radio-styles');
      if (style) {
        style.remove();
      }
    };
  }, []);

  // Selection configuration
  const rowSelection: TableProps<any>['rowSelection'] | undefined = enableSelection ? {
    type: multiRowSelection ? 'checkbox' : 'radio',
    selectedRowKeys: selectedRows?.map((row, idx) => row.key || row.id || idx) || [],
    onSelect: (record: any, selected: boolean) => {
      if (!onSelectRows) return;
      
      if (selected) {
        onSelectRows(multiRowSelection 
          ? [...(selectedRows || []), record]
          : [record]
        );
      } else {
        onSelectRows((selectedRows || []).filter(r => 
          (r.key || r.id) !== (record.key || record.id)
        ));
      }
    },
    onSelectAll: (selected: boolean, selectedRowsData: any[], changeRows: any[]) => {
      if (!onSelectRows || !multiRowSelection) return;
      
      if (selected) {
        const newSelected = [...(selectedRows || []), ...changeRows];
        onSelectRows(newSelected);
      } else {
        const changeRowKeys = changeRows.map(row => row.key || row.id);
        onSelectRows((selectedRows || []).filter(row => 
          !changeRowKeys.includes(row.key || row.id)
        ));
      }
    },
  } : undefined;

  const paginationConfig = {
    current: (pagination?.pageIndex || 0) + 1,
    pageSize: pagination?.pageSize || 10,
    total: serverSidePagination ? rowCount : dataWithKeys.length,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number, range: [number, number]) => 
      `Showing ${range[0]}-${range[1]} of ${total} results`,
    pageSizeOptions: ['10', '20', '30', '40', '50'],
    onChange: (page: number, size: number) => {
      onPaginationChange?.({
        pageIndex: page - 1,
        pageSize: size,
      });
    },
    onShowSizeChange: (current: number, size: number) => {
      onPaginationChange?.({
        pageIndex: 0,
        pageSize: size,
      });
    },
    className: 'ant-pagination-dark',
  };

  if (isLoading) {
    return (
      <div className="grid-view-container">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="grid-view-container">
        <Empty 
          description="No data available"
          style={{ 
            color: 'var(--main-text-secondary)',
            padding: '48px',
          }}
        />
      </div>
    );
  }

  return (
    <div className="grid-view-container">
      {view === 'card' ? (
        <>
          {/* Card View */}
          <div className="grid-view-cards">
            {dataWithKeys.map((rowData, idx) => {
              const isSelected = selectedRows
                ? selectedRows.some((r) => (r.key || r.id) === (rowData.key || rowData.id))
                : false;
              
              // Create menu items for this card
              const items: MenuProps['items'] = rowActions && rowActions.length > 0 
                ? rowActions.map((action: any) => ({
                    key: action.id,
                    label: actionLoading === action.id ? 'Loading...' : (action.label || 'Action'),
                    disabled: actionLoading === action.id,
                    onClick: (e: any) => {
                      e.domEvent.stopPropagation(); // Prevent card click
                      if (onRowAction) {
                        onRowAction(action, rowData);
                      }
                    },
                  }))
                : undefined;
              
              return (
                <Card
                  key={rowData.key || idx}
                  className={`grid-view-card ${
                    isSelected ? 'grid-view-card-selected' : ''
                  }`}
                  size="small"
                  style={{
                    backgroundColor: 'var(--card-bg)',
                    borderColor: isSelected ? 'var(--main-accent)' : 'var(--card-border)',
                    cursor: enableSelection ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (!enableSelection) return;
                    if (onSelectRows && selectedRows) {
                      isSelected
                        ? onSelectRows(selectedRows.filter((r) => (r.key || r.id) !== (rowData.key || rowData.id)))
                        : onSelectRows(
                            multiRowSelection
                              ? [...selectedRows, rowData]
                              : [rowData]
                          );
                    }
                  }}
                >
                  {/* Card Header with Checkbox and Actions */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    {enableSelection && (
                      <Checkbox
                        checked={isSelected}
                        onChange={() => {}} // Handled by card click
                        onClick={(e) => e.stopPropagation()} // Prevent double-click
                      />
                    )}
                    
                    {items && items.length > 0 && onRowAction && (
                      <Dropdown 
                        menu={{ items }} 
                        trigger={['click']}
                        placement="bottomRight"
                      >
                        <Button 
                          type="text" 
                          icon={<MoreOutlined />} 
                          size="small"
                          style={{ 
                            color: 'var(--main-text)',
                            border: 'none',
                            marginLeft: 'auto',
                          }}
                          onClick={(e) => e.stopPropagation()} // Prevent card click
                        />
                      </Dropdown>
                    )}
                  </div>

                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {columns.slice(0, 8).map((col) => (
                      <div key={col} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ 
                          fontWeight: 600, 
                          color: 'var(--main-text-secondary)', 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {col}
                        </span>
                        <span style={{ 
                          color: 'var(--main-text)', 
                          textAlign: 'right',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '60%'
                        }}>
                          {String(rowData[col] ?? '')}
                        </span>
                      </div>
                    ))}
                  </Space>

                  {columns.length > 8 && (
                    <div style={{ 
                      textAlign: 'right', 
                      fontSize: '12px', 
                      color: 'var(--disabled-text)',
                      marginTop: 8
                    }}>
                      +{columns.length - 8} more fields
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </>
      ) : (
        /* Table View */
        <Table
          className="grid-view-table force-no-hover"
          columns={tableColumns}
          dataSource={dataWithKeys}
          rowSelection={rowSelection}
          pagination={serverSidePagination ? paginationConfig : { ...paginationConfig, total: dataWithKeys.length }}
          loading={isLoading}
          size="small"
          scroll={{ x: true }}
          onChange={(pagination, filters, sorter, extra) => {
            // Handle client-side sorting for non-server-side pagination
            if (!serverSidePagination) {
              // Ant Design handles client-side sorting automatically with the sorter functions we defined
              return;
            }
            
            // For server-side pagination, you would need to pass sorting info back to parent
            // This would require additional props like onSortChange
            console.debug('Server-side sorting not implemented yet', { sorter, extra });
          }}
          style={{
            backgroundColor: 'var(--card-bg)',
          }}
        />
      )}
    </div>
  );
}
